import { notification } from '@surecompanies/core_components'
import {
  useMutation,
  UseMutationOptions,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import epsClient from 'enrollment-platform/api/eps.client'
import { useState } from 'react'
import type { CustomerConfigKey } from 'modules/activity-log-service/constants'
import { logCustomerConfigurationChange } from '../utils/customerConfigurationActivityLog'

export const EMAIL_NOTIFICATION_KEYS = {
  enrollmentConfirmation: 'email.enrollment-confirmation.enabled',
  applicationVerified: 'email.application-verified.enabled',
} as const

export type EmailNotificationKey =
  (typeof EMAIL_NOTIFICATION_KEYS)[keyof typeof EMAIL_NOTIFICATION_KEYS]

// Email notification PUT keys → the matching toggle key (labels live in CUSTOMER_CONFIG_LABELS).
const EMAIL_NOTIFICATION_CONFIG_KEYS: Record<
  EmailNotificationKey,
  CustomerConfigKey
> = {
  [EMAIL_NOTIFICATION_KEYS.enrollmentConfirmation]:
    'isEnrollmentConfirmationEmailEnabled',
  [EMAIL_NOTIFICATION_KEYS.applicationVerified]:
    'isApplicationVerifiedEmailEnabled',
}

type Toggles = {
  isIchraNoticeEnabled: boolean
  isWelcomeEmailEnabled: boolean
  isCMv1Enabled: boolean
  isCMv2Enabled: boolean
  displayContributionsToEmployees: boolean
  stripeLink: string
  isEnrollmentReminderEmailEnabled: boolean
  isEnrollmentConfirmationEmailEnabled: boolean
  isApplicationVerifiedEmailEnabled: boolean
}

export type CensusApiVersion = 'v1' | 'v2'

type UpdateReponse = {
  message: string
  success: boolean
}

export default function useCoustomerConfiguration(
  customerId: string,
  companyName?: string
) {
  const queryClient = useQueryClient()

  // Every successful toggle change from Admin writes a customer-level activity log entry.
  const logChange = (settingKey: CustomerConfigKey, newValue: boolean) =>
    logCustomerConfigurationChange({
      queryClient,
      customerId,
      companyName,
      settingKey,
      newValue,
    })
  const [isUpdatingERContribution, setIsUpdatingERContribution] =
    useState(false)

  const queryToggles = () => {
    return useQuery<Toggles>({
      queryKey: ['toggles', customerId],
      queryFn: async () => {
        const response = await epsClient.get<Toggles>(
          `/customers/${customerId}/configuration/toggles`
        )
        return response.data
      },
      enabled: Boolean(customerId),
    })
  }

  const updateIchraNoticeMutation = (options?: {
    mutationOptions?: UseMutationOptions<
      UpdateReponse,
      Error,
      { isIchraNoticeEnabled: boolean }
    >
  }) => {
    return useMutation<UpdateReponse, Error, { isIchraNoticeEnabled: boolean }>(
      {
        mutationFn: async ({ isIchraNoticeEnabled }) => {
          const response = await epsClient.post<UpdateReponse>(
            `/customers/${customerId}/configuration/ichra-notice`,
            { isIchraNoticeEnabled }
          )
          if (!response.data.success) throw new Error(response.data.message)
          logChange('isIchraNoticeEnabled', isIchraNoticeEnabled)
          return response.data
        },

        onSuccess: () => {
          // Invalidate the toggles query to update the cache
          queryClient.invalidateQueries({ queryKey: ['toggles', customerId] })
          notification.success({
            message: 'Ichra Notice updated successfully',
          })
        },
        onError: (error) => {
          notification.error({
            message: error.message ?? 'Failed to update Ichra Notice',
          })
        },
        ...options?.mutationOptions,
      }
    )
  }

  const updateWelcomeEmailMutation = (options?: {
    mutationOptions?: UseMutationOptions<
      UpdateReponse,
      Error,
      { isWelcomeEmailEnabled: boolean }
    >
  }) => {
    return useMutation<
      UpdateReponse,
      Error,
      { isWelcomeEmailEnabled: boolean }
    >({
      mutationFn: async ({ isWelcomeEmailEnabled }) => {
        const response = await epsClient.post<UpdateReponse>(
          `/customers/${customerId}/configuration/welcome-email`,
          { isWelcomeEmailEnabled }
        )
        if (!response.data.success) throw new Error(response.data.message)
        logChange('isWelcomeEmailEnabled', isWelcomeEmailEnabled)
        return response.data
      },
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['toggles', customerId] })
        notification.success({
          message: 'Welcome Email updated successfully',
        })
      },
      onError: (error) => {
        notification.error({
          message: error.message ?? 'Failed to update Welcome Email',
        })
      },
      ...options?.mutationOptions,
    })
  }

  const updateReminderEmailMutation = (options?: {
    mutationOptions?: UseMutationOptions<
      UpdateReponse,
      Error,
      { isEnrollmentReminderEmailEnabled: boolean }
    >
  }) => {
    return useMutation<
      UpdateReponse,
      Error,
      { isEnrollmentReminderEmailEnabled: boolean }
    >({
      mutationFn: async ({ isEnrollmentReminderEmailEnabled }) => {
        const response = await epsClient.post<UpdateReponse>(
          `/customers/${customerId}/configuration/reminder-email`,
          { isEnrollmentReminderEmailEnabled }
        )
        if (!response.data.success) throw new Error(response.data.message)
        logChange(
          'isEnrollmentReminderEmailEnabled',
          isEnrollmentReminderEmailEnabled
        )
        return response.data
      },
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['toggles', customerId] })
        notification.success({
          message: 'Reminder Email updated successfully',
        })
      },
      onError: (error) => {
        notification.error({
          message: error.message ?? 'Failed to update Reminder Email',
        })
      },
      ...options?.mutationOptions,
    })
  }

  const updateCMNavigationMutation = (options?: {
    mutationOptions?: UseMutationOptions<
      UpdateReponse,
      Error,
      { isCMNavigationEnabled: boolean; version: CensusApiVersion }
    >
  }) => {
    return useMutation<
      UpdateReponse,
      Error,
      { isCMNavigationEnabled: boolean; version: CensusApiVersion }
    >({
      mutationFn: async ({ isCMNavigationEnabled, version }) => {
        const response = await epsClient.post<UpdateReponse>(
          `/customers/${customerId}/configuration/cm-navigation`,
          { isCMNavigationEnabled, version }
        )
        if (!response.data.success) throw new Error(response.data.message)
        logChange(
          version === 'v1' ? 'isCMv1Enabled' : 'isCMv2Enabled',
          isCMNavigationEnabled
        )
        return response.data
      },
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['toggles', customerId] })
        notification.success({
          message: 'CM Navigation updated successfully',
        })
      },
      onError: (error) => {
        notification.error({
          message: error.message ?? 'Failed to update CM Navigation',
        })
      },
      ...options?.mutationOptions,
    })
  }

  const updateCensusApiVersionMutation = (options?: {
    mutationOptions?: UseMutationOptions<
      UpdateReponse,
      Error,
      { version: CensusApiVersion }
    >
  }) => {
    return useMutation<UpdateReponse, Error, { version: CensusApiVersion }>({
      mutationFn: async ({ version }) => {
        const response = await epsClient.post<UpdateReponse>(
          `/customers/${customerId}/configuration/cm-v2-api`,
          { isCMv2APIEnabled: version === 'v2' }
        )
        if (!response.data.success) throw new Error(response.data.message)
        logChange('isCMv2APIEnabled', version === 'v2')
        return response.data
      },
      onSuccess: async () => {
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: ['toggles', customerId] }),
          queryClient.invalidateQueries({
            queryKey: ['hrisPayroll', customerId],
          }),
        ])
        notification.success({
          message: 'Census API version updated successfully',
        })
      },
      onError: (error) => {
        notification.error({
          message: error.message ?? 'Failed to update census API version',
        })
      },
      ...options?.mutationOptions,
    })
  }

  const updateEmailNotificationMutation = (options?: {
    mutationOptions?: UseMutationOptions<
      unknown,
      Error,
      { key: EmailNotificationKey; value: boolean; label: string }
    >
  }) => {
    return useMutation<
      unknown,
      Error,
      { key: EmailNotificationKey; value: boolean; label: string }
    >({
      mutationFn: async ({ key, value }) => {
        const response = await epsClient.put(
          `/customers/${customerId}/configurations/${key}`,
          { value }
        )
        logChange(EMAIL_NOTIFICATION_CONFIG_KEYS[key], value)
        return response.data
      },
      onSuccess: (_data, { label }) => {
        queryClient.invalidateQueries({ queryKey: ['toggles', customerId] })
        queryClient.invalidateQueries({
          queryKey: ['customerConfigurationInventory', customerId],
        })
        notification.success({
          message: `${label} updated successfully`,
        })
      },
      onError: (error, { label }) => {
        notification.error({
          message: error.message ?? `Failed to update ${label}`,
        })
      },
      ...options?.mutationOptions,
    })
  }

  const updateERContributionData = async (value: boolean) => {
    setIsUpdatingERContribution(true)

    await epsClient
      .put<UpdateReponse>(`/customers/${customerId}/display-contributions`, {
        displayContributionsToEmployees: value,
      })
      .then(async (response) => {
        logChange('displayContributionsToEmployees', value)
        await queryClient.invalidateQueries({
          queryKey: ['toggles', customerId],
        })

        notification.success({
          message: 'ER Contribution updated successfully',
        })

        return response.data
      })
      .catch((error: any) => {
        notification.error({
          message: error?.message ?? 'Failed to update ER Contribution',
        })
      })
      .finally(() => {
        setIsUpdatingERContribution(false)
      })
  }

  return {
    queryToggles,
    updateIchraNoticeMutation,
    updateWelcomeEmailMutation,
    updateReminderEmailMutation,
    updateCMNavigationMutation,
    updateCensusApiVersionMutation,
    updateEmailNotificationMutation,
    updateERContribution: {
      updateERContributionData,
      isUpdatingERContribution,
    },
  }
}
